import { type FC, type ComponentProps } from "react";
import { TextArea } from "@root/shared/components/TextArea";
import { SelectBox } from "@root/shared/components/SelectBox";
import { TextField } from "@root/shared/components/TextField";
import { CheckboxAndText } from "@clientFrontend/features/publication/components/WelfareInputsCheckboxAndText";
import { Checkbox } from "@root/shared/components/Checkbox";
import { InformationText } from "@root/shared/components/InformationText";
import { Label } from "@root/shared/components/Label";
import * as commonStyles from "@clientFrontend/features/publication/styles/styles.css";
import { type BonusAvailableInputsProps } from "./type";
import * as styles from "./styles.css";

type Props = BonusAvailableInputsProps & ComponentProps<typeof CheckboxAndText>;

export const Presentation: FC<Props> = ({
  text,
  inputValues,
  formNames,
  bonusPaymentYearSelectOptions,
  bonusPaymentCountSelectOptions,
  bonusCheckboxChecked,
  handleChangeBonusCheckbox,
  handleInputChange,
  parentCheckboxId,
  errors,
  value,
}) => {
  return (
    <div className={commonStyles.container}>
      <div className={styles.checkboxContainer}>
        <CheckboxAndText
          text={text}
          checked={bonusCheckboxChecked}
          hasError={errors && Object.keys(errors).length > 0}
          onChange={(e) => handleChangeBonusCheckbox(e.target.checked)}
          id={parentCheckboxId}
          name="work_requirement[welfares][]"
          value={value}
        />
        <p className={styles.bonusDescription}>夏や冬など決まった時期に支給されるもの</p>
      </div>
      <div
        className={bonusCheckboxChecked ? commonStyles.detailForm : commonStyles.detailFormDisabled}
      >
        <section className={commonStyles.detailFormSection}>
          <h3 className={commonStyles.detailFormTitle}>
            支給実績
            {bonusCheckboxChecked && <Label theme="red">必須</Label>}
          </h3>
          <div className={commonStyles.detailFormBody}>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label className={styles.notYetProvidedCheckboxContainer}>
              <Checkbox
                name={formNames.bonusNoPaymentPastFlag}
                id={formNames.bonusNoPaymentPastFlag}
                checked={inputValues?.bonusNoPaymentPastFlag}
                onChange={(e) => handleInputChange("bonusNoPaymentPastFlag", e.target.checked)}
                hasError={Boolean(errors?.bonusNoPaymentPastFlag)}
                value={formNames.bonusNoPaymentPastFlag}
                disabled={!bonusCheckboxChecked}
              />
              <span>記載時点では支給実績なし</span>
            </label>
            <div className={styles.flexContainer}>
              <dl>
                <dt>
                  <h4 className={styles.detailFormComponentsLabel}>支給年度</h4>
                </dt>
                <dd>
                  <SelectBox
                    className={commonStyles.selectBox}
                    name={formNames.bonusPaymentYear}
                    id={formNames.bonusPaymentYear}
                    options={bonusPaymentYearSelectOptions}
                    hasError={Boolean(errors?.bonusPaymentYear)}
                    disabled={!bonusCheckboxChecked || inputValues?.bonusNoPaymentPastFlag}
                    value={inputValues?.bonusPaymentYear || ""}
                    selected={!!inputValues?.bonusPaymentYear}
                    onChange={(e) => handleInputChange("bonusPaymentYear", e.target.value)}
                    placeholder="年度"
                  />
                  <span className={styles.unit}>年</span>
                </dd>
              </dl>
              <dl className={styles.numberOfPayments}>
                <dt>
                  <h4 className={styles.detailFormComponentsLabel}>支給回数(年)</h4>
                </dt>
                <dd>
                  <SelectBox
                    className={commonStyles.selectBox}
                    name={formNames.bonusPaymentCount}
                    id={formNames.bonusPaymentCount}
                    options={bonusPaymentCountSelectOptions}
                    hasError={Boolean(errors?.bonusPaymentCount)}
                    disabled={!bonusCheckboxChecked || inputValues?.bonusNoPaymentPastFlag}
                    selected={!!inputValues?.bonusPaymentCount}
                    value={inputValues?.bonusPaymentCount}
                    onChange={(e) => handleInputChange("bonusPaymentCount", e.target.value)}
                    placeholder="回数"
                  />
                  <span className={styles.unit}>回</span>
                </dd>
              </dl>
            </div>
            {errors?.bonusPaymentYear && (
              <InformationText
                className={commonStyles.error}
                isAlert
                text={errors.bonusPaymentYear}
              />
            )}
            {errors?.bonusPaymentCount && (
              <InformationText
                className={commonStyles.error}
                isAlert
                text={errors.bonusPaymentCount}
              />
            )}
            <dl className={styles.rowSpacingTop}>
              <dt>
                <h4 className={styles.detailFormComponentsLabel}>支給総額(年)</h4>
              </dt>
              <dd className={commonStyles.detailItem}>
                <div className={styles.flexContainer}>
                  <span>
                    <TextField
                      type="text"
                      name={formNames.bonusPaymentTotalAmountMin}
                      id={formNames.bonusPaymentTotalAmountMin}
                      className={styles.textField}
                      value={inputValues?.bonusPaymentTotalAmountMin || ""}
                      hasError={Boolean(errors?.bonusPaymentTotalAmountMin)}
                      placeholder="入力してください"
                      disabled={!bonusCheckboxChecked || inputValues?.bonusNoPaymentPastFlag}
                      onChange={(e) =>
                        handleInputChange("bonusPaymentTotalAmountMin", e.target.value)
                      }
                    />
                    <span className={styles.unit}>円</span>
                    <span className={styles.dash}>〜</span>
                  </span>
                  <span>
                    <TextField
                      type="text"
                      name={formNames.bonusPaymentTotalAmountMax}
                      id={formNames.bonusPaymentTotalAmountMax}
                      className={styles.textField}
                      value={inputValues?.bonusPaymentTotalAmountMax}
                      hasError={Boolean(errors?.bonusPaymentTotalAmountMax)}
                      placeholder="入力してください"
                      disabled={!bonusCheckboxChecked || inputValues?.bonusNoPaymentPastFlag}
                      onChange={(e) =>
                        handleInputChange("bonusPaymentTotalAmountMax", e.target.value)
                      }
                    />
                    <span className={styles.unit}>円</span>
                  </span>
                </div>
              </dd>
            </dl>
            {errors?.bonusPaymentTotalAmountMin && (
              <InformationText
                className={commonStyles.error}
                isAlert
                text={errors.bonusPaymentTotalAmountMin}
              />
            )}
            {errors?.bonusPaymentTotalAmountMax && (
              <InformationText
                className={commonStyles.error}
                isAlert
                text={errors.bonusPaymentTotalAmountMax}
              />
            )}
          </div>
        </section>

        <section className={commonStyles.detailFormSection}>
          <h3 className={commonStyles.detailFormTitle}>
            支給条件
            {bonusCheckboxChecked && <Label theme="red">必須</Label>}
          </h3>
          <div className={commonStyles.detailFormBodySingleBox}>
            <dl className={commonStyles.detailFormItemMain}>
              <dd className={commonStyles.detailItem}>
                <TextArea
                  name={formNames.bonusPaymentConditions}
                  id={formNames.bonusPaymentConditions}
                  value={inputValues?.bonusPaymentConditions}
                  hasError={Boolean(errors?.bonusPaymentConditions)}
                  placeholder="入力してください"
                  size="small"
                  disabled={!bonusCheckboxChecked}
                  onChange={(e) => handleInputChange("bonusPaymentConditions", e.target.value)}
                />
              </dd>
            </dl>
          </div>
          {errors?.bonusPaymentConditions && (
            <InformationText
              className={commonStyles.error}
              isAlert
              text={errors.bonusPaymentConditions}
            />
          )}
        </section>
      </div>
    </div>
  );
};
